import { useState } from 'react';
import { Monitor, LogOut, Loader2 } from 'lucide-react';
import { Link, Navigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useLogoutMutation } from '../store/api/authApi';
import { REFRESH_TOKEN_KEY } from '../store/slices/authSlice';
import FullScreenLoader from '../components/FullScreenLoader';

const steps = [
  {
    number: 1,
    title: 'Download the Agent',
    description: 'Get the desktop app for your operating system below.',
  },
  {
    number: 2,
    title: 'Sign In',
    description: 'Open the agent and sign in with the same Google account.',
  },
  {
    number: 3,
    title: 'Configure Printer',
    description: 'Select your default printer in the agent settings.',
  },
  {
    number: 4,
    title: 'Generate QR Code',
    description: 'Create a QR code and display it at your shop counter.',
  },
];

const downloadLinks = {
  windows:
    'https://github.com/Dk007ms/Statioprint-Agent/releases/latest/download/StatioPrint-Agent-windows-setup.exe',
  // mac: 'https://github.com/Dk007ms/Statioprint-Agent/releases/latest/download/StatioPrint-Agent-macos.dmg', // macOS is currently not supported.
  linux:
    'https://github.com/Dk007ms/Statioprint-Agent/releases/latest/download/StatioPrint-Agent-linux.AppImage',
};

export default function SellerDashboardPage() {
  const { user, isAuthenticated, isLoading } = useAuth();
  const [logout] = useLogoutMutation();
  const [loggingOut, setLoggingOut] = useState(false);

  if (isLoading) {
    return (
      <div className="flex flex-col justify-center items-center min-h-screen gap-4 bg-slate-950">
        <Loader2 size={32} className="text-indigo-600 animate-spin" />
        <p className="text-sm text-slate-400">Loading...</p>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/seller" replace />;
  }

  const handleLogout = async () => {
    setLoggingOut(true);
    try {
      const rt = localStorage.getItem(REFRESH_TOKEN_KEY);
      await logout({ refreshToken: rt ?? undefined });
      window.location.href = '/seller';
    } catch {
      setLoggingOut(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-950 to-slate-800">
      <div className="max-w-3xl mx-auto px-4 pt-8 pb-16">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-8">
          <div className="flex items-center gap-3 min-w-0">
            <img src={user?.picture} alt={user?.name} className="w-12 h-12 rounded-full shrink-0" />
            <div className="min-w-0">
              <h2 className="text-lg font-semibold text-slate-200 leading-tight break-words">
                Welcome, {user?.name}
              </h2>
              <p className="text-sm text-slate-400 break-all">{user?.email}</p>
            </div>
          </div>
          <button
            onClick={handleLogout}
            disabled={loggingOut}
            className="inline-flex items-center justify-center gap-2 px-4 py-2 text-sm border border-slate-700/50 text-slate-400 rounded-lg hover:bg-slate-700/50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed w-full sm:w-auto"
          >
            <LogOut size={16} />
            Sign Out
          </button>
        </div>

        {/* Download Agent */}
        <div className="p-5 sm:p-8 mb-8 text-center bg-slate-800/60 backdrop-blur-sm rounded-xl border border-slate-700/50">
          <h2 className="text-2xl font-bold text-slate-200 mb-2 text-balance">
            Download the StatioPrint Agent
          </h2>
          <p className="text-sm text-slate-400 mb-6 max-w-md mx-auto">
            Install the desktop agent to connect your printer and start receiving orders.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <a
              href={downloadLinks.windows}
              className="inline-flex items-center justify-center gap-2 px-4 py-2.5 font-semibold text-white rounded-lg bg-indigo-600 hover:bg-indigo-700 border border-indigo-700 shadow-sm transition-colors"
            >
              <Monitor size={18} />
              Windows (.exe)
            </a>
            <a
              href={downloadLinks.linux}
              className="inline-flex items-center justify-center gap-2 px-4 py-2.5 font-semibold text-white rounded-lg bg-indigo-700 hover:bg-indigo-800 border border-indigo-800 shadow-sm transition-colors"
            >
              Linux (.AppImage)
            </a>
          </div>
        </div>

        {/* Quick Setup Steps */}
        <h2 className="text-2xl font-bold text-slate-200 mb-6">Quick Setup</h2>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          {steps.map((step) => (
            <div
              key={step.number}
              className="p-6 flex flex-col gap-2 bg-slate-800/60 backdrop-blur-sm rounded-xl border border-slate-700/50"
            >
              <div className="flex items-center gap-3">
                <div className="w-7 h-7 rounded-full bg-indigo-600/20 flex items-center justify-center text-xs font-bold text-indigo-400">
                  {step.number}
                </div>
                <h3 className="text-base font-semibold text-slate-200">{step.title}</h3>
              </div>
              <p className="text-sm text-slate-400 leading-relaxed pl-10">{step.description}</p>
            </div>
          ))}
        </div>
      </div>

      <footer className="border-t border-slate-800 bg-slate-900/50">
        <div className="max-w-3xl mx-auto px-4 py-8">
          <div className="flex flex-wrap justify-center gap-x-6 gap-y-2 text-sm text-slate-500">
            <Link to="/terms" className="hover:text-indigo-400 transition-colors">
              Terms & Conditions
            </Link>
            <Link to="/privacy" className="hover:text-indigo-400 transition-colors">
              Privacy Policy
            </Link>
            <Link to="/shipping" className="hover:text-indigo-400 transition-colors">
              Shipping Policy
            </Link>
            <Link to="/refund" className="hover:text-indigo-400 transition-colors">
              Cancellation & Refunds
            </Link>
            <Link to="/contact" className="hover:text-indigo-400 transition-colors">
              Contact Us
            </Link>
          </div>
          <p className="text-center text-xs text-slate-600 mt-4">
            StatioPrint by StatioMart | info@statiomart.com
          </p>
        </div>
      </footer>

      {/* Full Screen Loader */}
      <FullScreenLoader isVisible={loggingOut} message="Signing out..." subMessage="Please wait" />
    </div>
  );
}
